/**
 * RETOBLOCK PYTHON PROGRAMMING GUIDE DATA
 * Cấu trúc dữ liệu chuẩn cho Tài liệu Hướng dẫn Lập trình Python:
 * - Phần 1: Các lệnh dùng chung cho tất cả các bộ kit (Python core, time, urandom, flow control...)
 * - Phần 2: Các lệnh dùng riêng cho từng bộ kit (Hiện tại: Bộ Kit RETOLOGIX đầy đủ 100% lệnh)
 * - Quản trị nội dung: Hỗ trợ lưu trữ localStorage, xuất/nhập JSON và đồng bộ Supabase.
 */

import { supabaseService } from '../core/supabase-client.js';

export const STORAGE_KEY = 'retoblock_python_guide_data';
export const CONFIG_KEY = 'python_guide_data';
export const SYNC_CHANNEL = 'retoblock_guide_sync';

// DỮ LIỆU GỐC MẪU CHUẨN (DEFAULT DATA)
export const DEFAULT_GUIDE_DATA = {
  // PHẦN 1: CÁC LỆNH DÙNG CHUNG CHO TẤT CẢ CÁC BỘ KIT
  generalSection: {
    id: 'general',
    title: 'Phần 1: Lệnh Dùng Chung Cho Mọi Bộ Kit',
    description: 'Các cú pháp lệnh chuẩn của ngôn ngữ lập trình Python và thư viện tiêu chuẩn nhúng trong vi điều khiển ESP32, áp dụng đồng nhất cho mọi dòng robot giáo dục RETOCODE.',
    categories: [
      {
        id: 'gen_structure',
        name: 'Cấu Trúc Chương Trình Chuẩn',
        badgeColor: '#38BDF8',
        commands: [
          {
            id: 'gen_basic_skeleton',
            name: 'Khung chương trình Python tiêu chuẩn',
            syntax: 'from <kit_sdk> import <RobotClass>\nimport time\n\nrobot = <RobotClass>()\ntry:\n    # Code thực thi của bạn\n    while True:\n        robot.sleep(0.01)\nexcept KeyboardInterrupt:\n    robot.stop()',
            description: 'Mẫu khung sườn mã nguồn chuẩn cho mọi chương trình điều khiển robot trong RETOCODE Studio. Bao gồm import thư viện, khởi tạo đối tượng robot, khối try-except để tự động phanh dừng an toàn khi người dùng nhấn nút STOP khẩn cấp trên giao diện web.',
            params: [],
            returns: 'Không',
            example: `# Khung chương trình chuẩn cho robot RETOCAR (Kit RETOLOGIX)
from retologix import RetoCar
import time

robot = RetoCar()

try:
    print("Robot bắt đầu hoạt động!")
    while True:
        # Thực hiện các tác vụ tuần hoàn
        robot.sleep(0.01) # Nhường CPU để không bị treo
except KeyboardInterrupt:
    # Tự động dừng xe khi bấm STOP trên web
    robot.stop()
    print("Chương trình đã kết thúc an toàn.")`,
            note: 'Luôn bọc vòng lặp chính trong khối try...except KeyboardInterrupt để vi điều khiển dừng động cơ ngay khi bạn ấn nút STOP.'
          }
        ]
      },
      {
        id: 'gen_control_flow',
        name: 'Vòng Lặp & Điều Khiển Luồng',
        badgeColor: '#F59E0B',
        commands: [
          {
            id: 'gen_while_true',
            name: 'Vòng lặp vô tận (while True)',
            syntax: 'while True:\n    <khối_lệnh>',
            description: 'Duy trì chương trình chạy lặp đi lặp lại liên tục cho đến khi nhận tín hiệu ngắt STOP hoặc gặp lệnh break. Đây là vòng lặp cơ bản nhất trong lập trình vi điều khiển và robot.',
            params: [],
            returns: 'Không',
            example: `# Xe tiến liên tục
while True:
    robot.move("FORWARD", 50)
    robot.sleep(0.01)`,
            note: 'Bắt buộc phải có robot.sleep() hoặc time.sleep() trong vòng lặp vô tận để tránh nghẽn luồng CPU (Watchdog Timer Trigger).'
          },
          {
            id: 'gen_while_cond',
            name: 'Vòng lặp có điều kiện (while điều_kiện)',
            syntax: 'while <biểu_thức_điều_kiện>:\n    <khối_lệnh>',
            description: 'Lặp lại khối lệnh bên trong chừng nào biểu thức điều kiện vẫn còn đúng (True). Khi điều kiện trở thành False, vòng lặp sẽ tự động dừng.',
            params: [
              { name: 'biểu_thức_điều_kiện', type: 'bool', desc: 'Biểu thức so sánh hoặc hàm trả về True/False' }
            ],
            returns: 'Không',
            example: `# Xe tiến chừng nào khoảng cách phía trước còn lớn hơn 20cm
while robot.get_distance() > 20:
    robot.move("FORWARD", 60)
    robot.sleep(0.01)

robot.stop()`,
            note: 'Hãy chắc chắn rằng biến trong điều kiện được cập nhật bên trong vòng lặp để tránh bị treo vô tận.'
          },
          {
            id: 'gen_for_range',
            name: 'Vòng lặp đếm số lần (for i in range)',
            syntax: 'for i in range(so_lan):\n    <khối_lệnh>',
            description: 'Lặp lại một số lần xác định trước. Biến i sẽ nhận các giá trị từ 0 đến so_lan - 1.',
            params: [
              { name: 'so_lan', type: 'int', desc: 'Số lần muốn lặp lại khối lệnh' }
            ],
            returns: 'Không',
            example: `# Nháy đèn RGB đỏ 3 lần
for i in range(3):
    robot.set_rgb_color("RED")
    robot.sleep(0.3)
    robot.set_rgb_color("OFF")
    robot.sleep(0.3)`,
            note: 'Có thể sử dụng range(start, stop, step) để đếm bước nhảy, ví dụ range(1, 10, 2).'
          },
          {
            id: 'gen_break_continue',
            name: 'Lệnh thoát (break) & Nhảy cóc (continue)',
            syntax: 'break\ncontinue',
            description: 'Lệnh break dùng để thoát khỏi vòng lặp ngay lập tức. Lệnh continue dùng để bỏ qua các câu lệnh còn lại trong chu kỳ hiện tại và chuyển ngay sang chu kỳ lặp kế tiếp.',
            params: [],
            returns: 'Không',
            example: `while True:
    dist = robot.get_distance()
    if dist < 10:
        # Nếu quá gần vật cản, thoát vòng lặp ngay
        break
    robot.move("FORWARD", 50)
    robot.sleep(0.01)

robot.stop()`,
            note: 'Thường kết hợp bên trong câu lệnh if để xử lý các tình huống ngắt đột ngột.'
          }
        ]
      },
      {
        id: 'gen_conditionals',
        name: 'Rẽ Nhánh Điều Kiện (if / elif / else)',
        badgeColor: '#10B981',
        commands: [
          {
            id: 'gen_if_elif_else',
            name: 'Cấu trúc điều kiện if - elif - else',
            syntax: 'if <điều_kiện_1>:\n    <khối_lệnh_1>\nelif <điều_kiện_2>:\n    <khối_lệnh_2>\nelse:\n    <khối_lệnh_mặc_định>',
            description: 'Kiểm tra lần lượt các điều kiện logic từ trên xuống dưới. Nếu điều kiện nào đúng (True), khối lệnh tương ứng sẽ được thực thi và bỏ qua các nhánh còn lại.',
            params: [
              { name: 'điều_kiện', type: 'bool', desc: 'Biểu thức so sánh hoặc logic (True/False)' }
            ],
            returns: 'Không',
            example: `color = robot.get_color_name()

if color == "RED":
    robot.stop()
    robot.set_rgb_color("RED")
elif color == "GREEN":
    robot.move("FORWARD", 80)
    robot.set_rgb_color("GREEN")
else:
    robot.move("FORWARD", 40)
    robot.set_rgb_color("OFF")`,
            note: 'Chú ý thụt lề 4 dấu cách (indentation) cho mỗi khối lệnh trong Python.'
          }
        ]
      },
      {
        id: 'gen_time_module',
        name: 'Thư Viện Thời Gian (Module time)',
        badgeColor: '#8B5CF6',
        commands: [
          {
            id: 'gen_time_sleep',
            name: 'Tạm dừng chương trình theo giây (time.sleep)',
            syntax: 'time.sleep(seconds)',
            description: 'Tạm dừng thực thi mã nguồn trong khoảng thời gian xác định tính bằng giây. Hỗ trợ số thực (float) cho thời gian nhỏ hơn 1 giây.',
            params: [
              { name: 'seconds', type: 'int | float', desc: 'Số giây cần tạm dừng (ví dụ: 1, 0.5, 0.02)' }
            ],
            returns: 'None',
            example: `import time

# Chờ 0.5 giây (500 mili-giây)
time.sleep(0.5)`,
            note: 'Trong RETOBLOCK, bạn cũng có thể dùng robot.sleep(seconds) để vừa chờ vừa lắng nghe nút Stop an toàn.'
          },
          {
            id: 'gen_time_sleep_ms',
            name: 'Tạm dừng theo mili-giây (time.sleep_ms)',
            syntax: 'time.sleep_ms(ms)',
            description: 'Tạm dừng thực thi chương trình tính bằng đơn vị mili-giây (1/1000 giây).',
            params: [
              { name: 'ms', type: 'int', desc: 'Số mili-giây cần chờ (ví dụ: 200, 1000)' }
            ],
            returns: 'None',
            example: `import time

# Chờ 250 mili-giây
time.sleep_ms(250)`,
            note: 'Thích hợp cho các khoảng trễ ngắn của cảm biến.'
          },
          {
            id: 'gen_time_ticks_ms',
            name: 'Đồng hồ bấm giờ mili-giây (time.ticks_ms & ticks_diff)',
            syntax: 't_start = time.ticks_ms()\ntime.ticks_diff(time.ticks_ms(), t_start)',
            description: 'Đọc số mili-giây kể từ khi bo mạch khởi động và tính độ chênh lệch thời gian mà không làm dừng đứng (blocking) chương trình.',
            params: [
              { name: 't1', type: 'int', desc: 'Mốc thời gian hiện tại' },
              { name: 't2', type: 'int', desc: 'Mốc thời gian trước đó' }
            ],
            returns: 'int (khoảng thời gian mili-giây)',
            example: `import time

start = time.ticks_ms()

# Thực hiện công việc mà không làm dừng CPU
while time.ticks_diff(time.ticks_ms(), start) < 3000:
    robot.move("FORWARD", 50)
    robot.sleep(0.01)

robot.stop()`,
            note: 'Giải pháp hoàn hảo để lập trình đa nhiệm (Non-blocking timing).'
          }
        ]
      },
      {
        id: 'gen_urandom_module',
        name: 'Số Ngẫu Nhiên Phần Cứng (Module urandom)',
        badgeColor: '#EC4899',
        commands: [
          {
            id: 'gen_urandom_randint',
            name: 'Sinh số nguyên ngẫu nhiên (urandom.randint)',
            syntax: 'urandom.randint(a, b)',
            description: 'Sinh một số nguyên ngẫu nhiên trong đoạn từ a đến b (bao gồm cả hai đầu mút a và b). Tận dụng bộ tạo số ngẫu nhiên phần cứng True RNG của ESP32.',
            params: [
              { name: 'a', type: 'int', desc: 'Giá trị nhỏ nhất' },
              { name: 'b', type: 'int', desc: 'Giá trị lớn nhất' }
            ],
            returns: 'int (số ngẫu nhiên trong khoảng [a, b])',
            example: `import urandom

# Chọn tốc độ ngẫu nhiên từ 30% đến 80%
toc_do = urandom.randint(30, 80)
robot.move("FORWARD", toc_do)`,
            note: 'Rất hữu ích cho các thuật toán robot thám hiểm tránh vật cản ngẫu nhiên.'
          },
          {
            id: 'gen_urandom_random',
            name: 'Sinh số thực ngẫu nhiên từ 0.0 đến 1.0',
            syntax: 'urandom.random()',
            description: 'Sinh một số thực dấu phẩy động ngẫu nhiên trong nửa khoảng [0.0, 1.0).',
            params: [],
            returns: 'float (từ 0.0 đến xấp xỉ 1.0)',
            example: `import urandom

# Xác suất 50% rẽ trái, 50% rẽ phải
if urandom.random() < 0.5:
    robot.move("LEFT", 50)
else:
    robot.move("RIGHT", 50)`,
            note: 'Dùng cho các thuật toán xác suất và trí tuệ nhân tạo đơn giản.'
          }
        ]
      },
      {
        id: 'gen_data_structures',
        name: 'Biến Số & Danh Sách (Lists)',
        badgeColor: '#06B6D4',
        commands: [
          {
            id: 'gen_lists_basic',
            name: 'Khởi tạo và thao tác danh sách (List)',
            syntax: 'danh_sach = []\ndanh_sach.append(gia_tri)\nlen(danh_sach)\ndanh_sach[vi_tri]',
            description: 'Danh sách dùng để lưu trữ một tập hợp nhiều giá trị theo thứ tự (ví dụ lịch sử các màu sắc đã quét, khoảng cách đo được).',
            params: [
              { name: 'gia_tri', type: 'any', desc: 'Phần tử cần thêm vào danh sách' }
            ],
            returns: 'Các thao tác trả về tương ứng',
            example: `# Ghi nhận màu sắc các trạm robot đi qua
lich_su_mau = []

while len(lich_su_mau) < 5:
    mau = robot.get_color_name()
    if mau != "UNKNOWN" and (len(lich_su_mau) == 0 or mau != lich_su_mau[-1]):
        lich_su_mau.append(mau)
        print("Đã phát hiện trạm màu:", mau)
    robot.sleep(0.1)`,
            note: 'Chỉ số danh sách trong Python bắt đầu từ 0 (phần tử đầu tiên là danh_sach[0]).'
          }
        ]
      },
      {
        id: 'gen_functions',
        name: 'Hàm Tự Định Nghĩa (Functions)',
        badgeColor: '#6366F1',
        commands: [
          {
            id: 'gen_def_function',
            name: 'Khai báo hàm với từ khóa def',
            syntax: 'def ten_ham(tham_so_1, tham_so_2):\n    <khối_lệnh>\n    return ket_qua',
            description: 'Gộp các đoạn mã lặp lại thành một hàm để tái sử dụng, giúp code gọn gàng, sáng sủa và dễ bảo trì.',
            params: [
              { name: 'tham_so', type: 'tùy chọn', desc: 'Dữ liệu đầu vào truyền cho hàm' }
            ],
            returns: 'Giá trị trả về thông qua câu lệnh return (nếu có)',
            example: `# Định nghĩa hàm quay xe 90 độ sang phải
def quay_phai_90():
    robot.move("RIGHT", 60)
    robot.sleep(0.45) # Thời gian xoay thực tế
    robot.stop()

# Gọi hàm
quay_phai_90()`,
            note: 'Nếu muốn thay đổi giá trị của biến số toàn cục bên trong hàm, dùng từ khóa global ten_bien.'
          }
        ]
      }
    ]
  },

  // PHẦN 2: CÁC BỘ KIT VÀ LỆNH DÙNG RIÊNG
  kits: [
    {
      id: 'RETOLOGIX',
      name: 'Bộ Kit RETOLOGIX (Robot Xe Thông Minh STEAM)',
      badge: 'RETOLOGIX STEAM V1.0',
      badgeColor: '#23A9E1',
      description: 'Kit robot xe tự hành RETOLOGIX tích hợp vi điều khiển kép ESP32, 2 động cơ DC encoder/PWM, cảm biến siêu âm, 3 mắt dò line hồng ngoại, cảm biến màu quang phổ TCS34725, servo góc, còi piezo và LED RGB đa màu.',
      sdkImport: 'from retologix import RetoCar\nrobot = RetoCar()',
      categories: [
        {
          id: 'kit_movement',
          name: 'Di Chuyển & Động Cơ (Movement & Motors)',
          badgeColor: '#3B82F6',
          commands: [
            {
              id: 'retocar_move',
              name: 'robot.move(direction, speed)',
              syntax: 'robot.move(direction="FORWARD", speed=50)',
              description: 'Điều khiển cả 2 bánh xe của robot di chuyển theo một trong 4 hướng chính với tốc độ tùy chỉnh từ 0% đến 100%.',
              params: [
                { name: 'direction', type: 'str', desc: '"FORWARD" (Tiến), "BACKWARD" (Lùi), "LEFT" (Rẽ trái), "RIGHT" (Rẽ phải)', default: '"FORWARD"' },
                { name: 'speed', type: 'int', desc: 'Tốc độ động cơ từ 0 đến 100 (%)', default: '50' }
              ],
              returns: 'None',
              example: `# Xe tiến với tốc độ 70% trong 2 giây rồi rẽ phải
robot.move("FORWARD", 70)
robot.sleep(2.0)
robot.move("RIGHT", 50)
robot.sleep(0.5)
robot.stop()`,
              note: 'Khi rẽ trái hoặc rẽ phải, hai bánh xe sẽ quay ngược chiều nhau (Spin Turn) giúp xe xoay quanh tâm một cách linh hoạt.'
            },
            {
              id: 'retocar_set_motor',
              name: 'robot.set_motor(motor, direction, speed)',
              syntax: 'robot.set_motor(motor="LEFT", direction="FORWARD", speed=50)',
              description: 'Điều khiển độc lập một kênh động cơ bánh xe bên trái hoặc bên phải. Rất hữu ích cho thuật toán dò line vi sai hoặc bám tường mượt mà.',
              params: [
                { name: 'motor', type: 'str', desc: '"LEFT" (Bánh trái) hoặc "RIGHT" (Bánh phải)', default: '"LEFT"' },
                { name: 'direction', type: 'str', desc: '"FORWARD" (Quay tiến) hoặc "BACKWARD" (Quay lùi)', default: '"FORWARD"' },
                { name: 'speed', type: 'int', desc: 'Tốc độ động cơ từ 0 đến 100 (%)', default: '50' }
              ],
              returns: 'None',
              example: `# Lượn vòng cung mềm mại: bánh trái chạy nhanh hơn bánh phải
robot.set_motor("LEFT", "FORWARD", 80)
robot.set_motor("RIGHT", "FORWARD", 40)
robot.sleep(1.5)
robot.stop()`,
              note: 'Có thể kết hợp đặt bánh trái tiến và bánh phải tiến với tốc độ khác nhau để xe rẽ vòng tròn lớn.'
            },
            {
              id: 'retocar_stop',
              name: 'robot.stop()',
              syntax: 'robot.stop()',
              description: 'Phanh dừng khẩn cấp cả 2 động cơ ngay lập tức (đưa xung PWM về 0 và khóa chốt trạng thái dừng an toàn).',
              params: [],
              returns: 'None',
              example: `# Dừng robot ngay lập tức
robot.stop()`,
              note: 'Nên gọi robot.stop() trong khối except KeyboardInterrupt để xe không chạy tự do khi bị ngắt code.'
            },
            {
              id: 'retocar_set_arm_angle',
              name: 'robot.set_arm_angle(angle) / set_servo_angle',
              syntax: 'robot.set_arm_angle(angle)\nrobot.set_servo_angle(angle)',
              description: 'Điều khiển góc quay của động cơ Servo PWM (kết nối cổng servo mở rộng, dùng điều khiển cánh tay gắp, tấm chắn hoặc hướng camera).',
              params: [
                { name: 'angle', type: 'int', desc: 'Góc quay từ 0 đến 180 độ', default: '90' }
              ],
              returns: 'None',
              example: `# Nâng cánh tay lên 120 độ, chờ 1s rồi hạ xuống 30 độ
robot.set_arm_angle(120)
robot.sleep(1.0)
robot.set_arm_angle(30)`,
              note: 'Hàm robot.set_servo_angle(angle) là bí danh tương đương 100% với set_arm_angle.'
            }
          ]
        },
        {
          id: 'kit_sensors',
          name: 'Cảm Biến Ngoại Vi (Sensors)',
          badgeColor: '#0EA5E9',
          commands: [
            {
              id: 'retocar_get_distance',
              name: 'robot.get_distance()',
              syntax: 'robot.get_distance()',
              description: 'Đọc khoảng cách từ cảm biến siêu âm HC-SR04 gắn phía trước đầu xe đến vật cản gần nhất tính theo đơn vị centimet.',
              params: [],
              returns: 'float (Khoảng cách cm, từ 2.0 đến 400.0 cm)',
              example: `# Tự động phanh khi gặp vật cản cách dưới 15cm
while True:
    khoang_cach = robot.get_distance()
    if khoang_cach < 15:
        robot.stop()
        robot.buzzer_tone(2000, 200)
    else:
        robot.move("FORWARD", 50)
    robot.sleep(0.02)`,
              note: 'Nếu cảm biến không nhận được sóng phản hồi hoặc bị che khuất vượt ngưỡng, giá trị trả về sẽ là 999.0 cm.'
            },
            {
              id: 'retocar_get_line',
              name: 'robot.get_line(sensor_idx)',
              syntax: 'robot.get_line(sensor_idx=1)',
              description: 'Đọc giá trị từ một mắt cảm biến dò line hồng ngoại (1: Mắt Trái, 2: Mắt Giữa, 3: Mắt Phải). Trả về True nếu phát hiện vạch đen hấp thụ tia hồng ngoại.',
              params: [
                { name: 'sensor_idx', type: 'int', desc: '1: Mắt Trái (Left), 2: Mắt Giữa (Center), 3: Mắt Phải (Right)', default: '1' }
              ],
              returns: 'bool (True: phát hiện vạch đen, False: nền trắng)',
              example: `# Dò line 2 mắt đơn giản
while True:
    trai = robot.get_line(1)
    phai = robot.get_line(3)

    if trai and not phai:
        robot.move("LEFT", 40)
    elif phai and not trai:
        robot.move("RIGHT", 40)
    else:
        robot.move("FORWARD", 50)
    robot.sleep(0.01)`,
              note: 'Vạch đen hấp thụ hồng ngoại trả về True; nền trắng phản xạ tia hồng ngoại trả về False.'
            },
            {
              id: 'retocar_get_all_line',
              name: 'robot.get_all_line()',
              syntax: 'robot.get_all_line()',
              description: 'Đọc đồng thời trạng thái cả 3 mắt dò line hồng ngoại trong một chu kỳ truy vấn duy nhất. Giúp tăng tốc độ xử lý cho các bài thi đua xe tốc độ cao.',
              params: [],
              returns: 'tuple (s1_trai, s2_giua, s3_phai) gồm 3 giá trị bool',
              example: `# Nhận diện giao lộ ngã tư khi cả 3 mắt cùng chạm vạch đen
while True:
    s1, s2, s3 = robot.get_all_line()
    if s1 and s2 and s3:
        # Đã đến ngã tư
        robot.stop()
        robot.buzzer_tone(1500, 300)
        break
    robot.move("FORWARD", 50)
    robot.sleep(0.01)`,
              note: 'Trả về dạng tuple 3 phần tử (bool, bool, bool).'
            },
            {
              id: 'retocar_get_light_level',
              name: 'robot.get_light_level()',
              syntax: 'robot.get_light_level()',
              description: 'Đọc cường độ ánh sáng môi trường xung quanh xe thông qua cảm biến quang trở (LDR) tích hợp trên mạch.',
              params: [],
              returns: 'int (Giá trị từ 0 - tối mịt đến 100 - cực sáng)',
              example: `# Tự động bật đèn pha khi trời tối
while True:
    anh_sang = robot.get_light_level()
    if anh_sang < 30:
        robot.set_rgb_color("WHITE") # Bật đèn pha
    else:
        robot.set_rgb_color("OFF")
    robot.sleep(0.1)`,
              note: 'Thích hợp cho các dự án thành phố thông minh (Smart City) và xe thông minh tự động bật đèn.'
            },
            {
              id: 'retocar_get_color_name',
              name: 'robot.get_color_name()',
              syntax: 'robot.get_color_name()',
              description: 'Nhận diện màu sắc của thẻ màu hoặc mặt đường bên dưới đầu đọc cảm biến I2C TCS34725 bằng thuật toán Auto-HSV 8 màu chuẩn STEAM.',
              params: [],
              returns: 'str ("RED", "GREEN", "BLUE", "YELLOW", "PURPLE", "BROWN", "WHITE", "BLACK", hoặc "UNKNOWN")',
              example: `# Điều hướng xe theo biển báo màu sắc
while True:
    mau = robot.get_color_name()
    if mau == "RED":
        robot.stop()
        print("Gặp biển báo ĐỎ: Dừng xe!")
    elif mau == "GREEN":
        robot.move("FORWARD", 60)
        print("Gặp biển báo XANH LÁ: Tăng tốc!")
    robot.sleep(0.05)`,
              note: 'Khoảng cách đọc màu tối ưu của cảm biến là từ 1cm đến 2.5cm so với bề mặt màu.'
            },
            {
              id: 'retocar_get_color_id',
              name: 'robot.get_color_id()',
              syntax: 'robot.get_color_id()',
              description: 'Trả về mã ID dạng số nguyên của màu sắc nhận diện được. Tiện lợi cho việc xử lý số học và lập chỉ mục mảng.',
              params: [],
              returns: 'int (0: UNKNOWN, 1: RED, 2: GREEN, 3: BLUE, 4: YELLOW, 5: PURPLE, 6: BROWN, 7: WHITE, 8: BLACK)',
              example: `# Kiểm tra nếu là màu Đỏ (ID = 1) hoặc Vàng (ID = 4)
cid = robot.get_color_id()
if cid == 1 or cid == 4:
    robot.buzzer_tone(1000, 100)`,
              note: '0 là không xác định, 1 đến 8 tương ứng 8 màu STEM tiêu chuẩn.'
            },
            {
              id: 'retocar_get_raw_rgb',
              name: 'robot.get_raw_rgb()',
              syntax: 'robot.get_raw_rgb()',
              description: 'Đọc bộ ba giá trị quang phổ màu Đỏ (R), Lục (G), Lam (B) sau khi đã chuẩn hóa và triệt tiêu nhiễu môi trường tối (Dark Offset Subtraction).',
              params: [],
              returns: 'tuple (r, g, b) gồm 3 số nguyên từ 0 đến 255',
              example: `# In giá trị RGB phục vụ nghiên cứu khoa học dữ liệu
r, g, b = robot.get_raw_rgb()
print(f"Giá trị màu RGB thực tế: R={r}, G={g}, B={b}")`,
              note: 'Các giá trị nằm trong dải 0 - 255 chuẩn quốc tế.'
            },
            {
              id: 'retocar_is_button_pressed',
              name: 'robot.is_button_pressed(btn_idx)',
              syntax: 'robot.is_button_pressed(btn_idx=1)',
              description: 'Kiểm tra trạng thái của nút nhấn cơ học người dùng tích hợp trên mạch vi điều khiển của robot.',
              params: [
                { name: 'btn_idx', type: 'int', desc: '1: Nút nhấn 1 (SW1), 2: Nút nhấn 2 (SW2)', default: '1' }
              ],
              returns: 'bool (True: đang được ấn giữ, False: đang nhả)',
              example: `# Chờ người dùng nhấn nút 1 mới bắt đầu chạy
print("Hãy nhấn nút 1 trên xe để khởi hành...")
while not robot.is_button_pressed(1):
    robot.sleep(0.05)

print("Đã nhấn nút! Xe xuất phát!")
robot.move("FORWARD", 60)`,
              note: 'Mạch đã tích hợp mạch khử rung phím phần cứng (Debounce).'
            }
          ]
        },
        {
          id: 'kit_feedback',
          name: 'Đèn & Còi Báo Hiệu (LEDs & Buzzer)',
          badgeColor: '#C026D3',
          commands: [
            {
              id: 'retocar_set_rgb_color',
              name: 'robot.set_rgb_color(color) / set_rgb_color(r, g, b)',
              syntax: 'robot.set_rgb_color("RED")\nrobot.set_rgb_color(255, 128, 0)',
              description: 'Điều khiển đèn LED RGB đa màu trên bo mạch chính. Hỗ trợ gọi theo tên màu phổ biến hoặc truyền trực tiếp 3 giá trị R, G, B từ 0 đến 255.',
              params: [
                { name: 'color', type: 'str | int', desc: 'Tên màu ("RED", "GREEN", "BLUE", "YELLOW", "CYAN", "MAGENTA", "WHITE", "OFF") HOẶC giá trị số r (0-255)' },
                { name: 'g', type: 'int (tùy chọn)', desc: 'Giá trị Green (0-255) khi truyền số', default: '0' },
                { name: 'b', type: 'int (tùy chọn)', desc: 'Giá trị Blue (0-255) khi truyền số', default: '0' }
              ],
              returns: 'None',
              example: `# Cách 1: Đặt màu theo tên
robot.set_rgb_color("BLUE")
robot.sleep(1)

# Cách 2: Đặt màu cam tùy chỉnh theo RGB (255, 128, 0)
robot.set_rgb_color(255, 128, 0)
robot.sleep(1)

# Tắt LED
robot.set_rgb_color("OFF")`,
              note: 'Để tắt LED RGB, truyền chuỗi "OFF" hoặc robot.set_rgb_color(0, 0, 0).'
            },
            {
              id: 'retocar_set_rgb_percent',
              name: 'Pha màu LED RGB theo phần trăm (0 - 100%)',
              syntax: `# Quy đổi phần trăm (%) sang 8-bit PWM (0 - 255):
# pwm = int(round(percent * 2.55))
robot.set_rgb_color(r_pwm, g_pwm, b_pwm)`,
              description: 'Pha trộn màu sắc tùy ý cho đèn LED RGB bằng cách điều chỉnh độ sáng từ 0% đến 100% cho từng bóng Đỏ (Red), Xanh lá (Green), Xanh dương (Blue). Giá trị phần trăm được quy đổi tự động sang chuẩn 8-bit PWM (0-255).',
              params: [
                { name: 'r_percent', type: 'int | float', desc: 'Độ sáng LED Đỏ từ 0% đến 100%' },
                { name: 'g_percent', type: 'int | float', desc: 'Độ sáng LED Xanh lá từ 0% đến 100%' },
                { name: 'b_percent', type: 'int | float', desc: 'Độ sáng LED Xanh dương từ 0% đến 100%' }
              ],
              returns: 'None',
              example: `# Hiệu ứng đèn thở (Breathing LED) màu Tím (Đỏ + Xanh dương)
# Tăng dần độ sáng từ 0% đến 100%
for brightness in range(0, 101, 5):
    r_val = int(round(brightness * 2.55))
    b_val = int(round(brightness * 2.55))
    robot.set_rgb_color(r_val, 0, b_val)
    robot.sleep(0.05)

# Giảm dần độ sáng từ 100% về 0%
for brightness in range(100, -1, -5):
    r_val = int(round(brightness * 2.55))
    b_val = int(round(brightness * 2.55))
    robot.set_rgb_color(r_val, 0, b_val)
    robot.sleep(0.05)

robot.set_rgb_color("OFF")`,
              note: 'Khi dùng Blockly, khối lệnh tự động sinh công thức tính toán và kẹp giới hạn an toàn (clamp 0-255).'
            },
            {
              id: 'retocar_set_indicator',
              name: 'robot.set_indicator(side, state)',
              syntax: 'robot.set_indicator(side="LEFT", state=True)',
              description: 'Bật hoặc tắt đèn LED xi nhan báo rẽ bên sườn trái hoặc sườn phải của xe.',
              params: [
                { name: 'side', type: 'str', desc: '"LEFT" (Đèn xi nhan trái) hoặc "RIGHT" (Đèn xi nhan phải)', default: '"LEFT"' },
                { name: 'state', type: 'bool', desc: 'True (Bật đèn sáng), False (Tắt đèn)', default: 'True' }
              ],
              returns: 'None',
              example: `# Bật xi nhan phải, rẽ phải rồi tắt xi nhan
robot.set_indicator("RIGHT", True)
robot.move("RIGHT", 50)
robot.sleep(0.6)
robot.stop()
robot.set_indicator("RIGHT", False)`,
              note: 'Giúp mô phỏng xe thông minh tuân thủ luật giao thông chân thực.'
            },
            {
              id: 'retocar_buzzer_tone',
              name: 'robot.buzzer_tone(frequency, duration_ms)',
              syntax: 'robot.buzzer_tone(frequency=1000, duration_ms=0)',
              description: 'Phát âm thanh qua còi buzzer piezo với tần số chỉ định (Hz) và thời lượng mili-giây.',
              params: [
                { name: 'frequency', type: 'int', desc: 'Tần số âm thanh (Hz), ví dụ: 262 (Đô), 294 (Rê), 330 (Mi), 1000, 2000...', default: '1000' },
                { name: 'duration_ms', type: 'int', desc: 'Thời lượng phát (ms). Nếu đặt 0 sẽ phát liên tục cho tới khi gọi buzzer_off()', default: '0' }
              ],
              returns: 'None',
              example: `# Phát tiếng bíp ngắn 2000Hz trong 150 mili-giây
robot.buzzer_tone(2000, 150)`,
              note: 'Dải tần số khuyến nghị từ 200Hz đến 5000Hz.'
            },
            {
              id: 'retocar_buzzer_off',
              name: 'robot.buzzer_off()',
              syntax: 'robot.buzzer_off()',
              description: 'Tắt còi buzzer ngay lập tức, ngắt hoàn toàn mọi âm thanh đang phát.',
              params: [],
              returns: 'None',
              example: `# Phát âm thanh liên tục rồi tắt
robot.buzzer_tone(1500)
robot.sleep(0.5)
robot.buzzer_off()`,
              note: 'Luôn gọi trong phần except KeyboardInterrupt để chống còi kêu dai dẳng khi dừng code.'
            }
          ]
        },
        {
          id: 'kit_time_events',
          name: 'Đồng Hồ Bấm Giờ & Xử Lý Sự Kiện (Time & Events)',
          badgeColor: '#F43F5E',
          commands: [
            {
              id: 'retocar_reset_timer',
              name: 'robot.reset_timer()',
              syntax: 'robot.reset_timer()',
              description: 'Khởi tạo lại mốc đếm thời gian nội bộ của robot về 0 giây.',
              params: [],
              returns: 'None',
              example: `# Bắt đầu tính giờ vòng đua
robot.reset_timer()`,
              note: 'Được tự động gọi ở đầu kịch bản MicroPython do RETOCODE tạo ra.'
            },
            {
              id: 'retocar_time_now',
              name: 'robot.time_now()',
              syntax: 'robot.time_now()',
              description: 'Trả về số giây đã trôi qua kể từ lần gọi reset_timer() gần nhất.',
              params: [],
              returns: 'float (Số giây trôi qua, ví dụ: 3.42)',
              example: `# Chạy xe trong đúng 5.0 giây rồi dừng
robot.reset_timer()
while robot.time_now() < 5.0:
    robot.move("FORWARD", 50)
    robot.sleep(0.01)

robot.stop()
print("Hoàn thành! Thời gian:", robot.time_now())`,
              note: 'Có độ chính xác cao dựa trên bộ đếm thời gian phần cứng của ESP32.'
            },
            {
              id: 'retocar_sleep',
              name: 'robot.sleep(seconds)',
              syntax: 'robot.sleep(seconds)',
              description: 'Tạm dừng chương trình theo giây nhưng vẫn giữ cho luồng xử lý sự kiện (Event Loop) và nút dừng khẩn cấp STOP hoạt động liên tục.',
              params: [
                { name: 'seconds', type: 'int | float', desc: 'Số giây cần tạm dừng (hỗ trợ số thực: 0.1, 1.5...)' }
              ],
              returns: 'None',
              example: `# Tiến 1.5 giây
robot.move("FORWARD", 60)
robot.sleep(1.5)
robot.stop()`,
              note: 'Khuyến nghị LUÔN sử dụng robot.sleep thay vì time.sleep khi lập trình robot RETOCAR để đảm bảo độ an toàn cao nhất.'
            },
            {
              id: 'retocar_process_events',
              name: 'robot.process_events()',
              syntax: 'robot.process_events()',
              description: 'Quét và kiểm tra các điều kiện để kích hoạt các hàm sự kiện (Event Handlers) đã được đăng ký thông qua Decorator.',
              params: [],
              returns: 'None',
              example: `# Duy trì vòng lặp kiểm tra sự kiện
while True:
    robot.process_events()
    robot.sleep(0.02)`,
              note: 'Nếu trong vòng lặp bạn đã dùng robot.sleep(), hàm robot.sleep() đã tự động gọi process_events() bên trong.'
            },
            {
              id: 'retocar_on_button',
              name: '@robot.on_button(btn, action)',
              syntax: '@robot.on_button(btn=1, action="PRESSED")\ndef khi_bam_nut():\n    <khối_lệnh>',
              description: 'Decorator định nghĩa hàm callback tự động thực thi khi nút nhấn 1 hoặc 2 trên xe được bấm (PRESSED) hoặc nhả ra (RELEASED).',
              params: [
                { name: 'btn', type: 'int', desc: '1 (Nút SW1) hoặc 2 (Nút SW2)', default: '1' },
                { name: 'action', type: 'str', desc: '"PRESSED" (Được bấm xuống) hoặc "RELEASED" (Nhả ra)', default: '"PRESSED"' }
              ],
              returns: 'None',
              example: `@robot.on_button(btn=1, action="PRESSED")
def khi_nhan_nut_1():
    print("Nút 1 được nhấn: Bắt đầu chạy!")
    robot.move("FORWARD", 60)

@robot.on_button(btn=2, action="PRESSED")
def khi_nhan_nut_2():
    print("Nút 2 được nhấn: Dừng xe!")
    robot.stop()

# Vòng lặp lắng nghe sự kiện
while True:
    robot.sleep(0.05)`,
              note: 'Cơ chế lập trình hướng sự kiện (Event-driven Programming) chuẩn quốc tế.'
            },
            {
              id: 'retocar_on_color',
              name: '@robot.on_color(color)',
              syntax: '@robot.on_color(color="RED")\ndef khi_thay_mau():\n    <khối_lệnh>',
              description: 'Decorator định nghĩa hàm callback tự động kích hoạt khi cảm biến màu phát hiện màu sắc chỉ định.',
              params: [
                { name: 'color', type: 'str', desc: '"RED", "GREEN", "BLUE", "YELLOW", "PURPLE", "BROWN", "WHITE", "BLACK"', default: '"RED"' }
              ],
              returns: 'None',
              example: `@robot.on_color("RED")
def khi_thay_vach_do():
    robot.stop()
    robot.buzzer_tone(2000, 300)
    print("Phát hiện vạch ĐỎ! Đã dừng lại.")

while True:
    robot.move("FORWARD", 40)
    robot.sleep(0.02)`,
              note: 'Sự kiện chỉ kích hoạt một lần tại thời điểm chuyển trạng thái từ màu khác sang màu này.'
            },
            {
              id: 'retocar_on_timer',
              name: '@robot.on_timer(seconds)',
              syntax: '@robot.on_timer(seconds=5)\ndef khi_het_gio():\n    <khối_lệnh>',
              description: 'Decorator kích hoạt hàm callback đúng một lần khi đồng hồ bấm giờ nội bộ của xe đạt mốc thời gian seconds.',
              params: [
                { name: 'seconds', type: 'int | float', desc: 'Thời gian mốc tính bằng giây', default: '5.0' }
              ],
              returns: 'None',
              example: `@robot.on_timer(seconds=10)
def sau_10_giay():
    robot.stop()
    robot.set_rgb_color("GREEN")
    print("Đã hết 10 giây bài thi!")

while True:
    robot.sleep(0.05)`,
              note: 'Rất hữu ích cho các bài toán giới hạn thời gian làm nhiệm vụ.'
            },
            {
              id: 'retocar_on_condition',
              name: '@robot.on_condition(cond_fn)',
              syntax: '@robot.on_condition(kiem_tra_dieu_kien)\ndef khi_thoa_man():\n    <khối_lệnh>',
              description: 'Decorator kích hoạt hàm callback khi hàm kiểm tra điều kiện logic cond_fn trả về True.',
              params: [
                { name: 'cond_fn', type: 'function', desc: 'Hàm logic trả về boolean True/False' }
              ],
              returns: 'None',
              example: `def gan_tuong():
    return robot.get_distance() < 12

@robot.on_condition(gan_tuong)
def xu_ly_gan_tuong():
    robot.stop()
    robot.buzzer_tone(1800, 200)

while True:
    robot.move("FORWARD", 50)
    robot.sleep(0.02)`,
              note: 'Cho phép bạn tự do sáng tạo mọi điều kiện kích hoạt sự kiện tùy ý.'
            }
          ]
        }
      ]
    }
  ]
};

// CÁC HÀM TIỆN ÍCH QUẢN LÝ DỮ LIỆU & QUYỀN ADMIN (DATABASE-FIRST)

/**
 * Đọc nhanh từ bộ đệm đồng bộ (synchronous cache) để render ngay lập tức
 */
export function getGuideData() {
  if (typeof window === 'undefined') return DEFAULT_GUIDE_DATA;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_GUIDE_DATA;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.kits || !parsed.generalSection) {
      return DEFAULT_GUIDE_DATA;
    }
    return parsed;
  } catch (e) {
    return DEFAULT_GUIDE_DATA;
  }
}

/**
 * Database-First: Nạp dữ liệu mới nhất từ bảng system_configs trên Supabase
 * Tự động cập nhật vào Offline Cache (localStorage). Fallback nếu mất mạng/chưa có DB.
 */
export async function fetchGuideData() {
  // 1. Database-First: Truy vấn bảng system_configs trên Supabase
  if (supabaseService && supabaseService.client) {
    try {
      const { data, error } = await supabaseService.client
        .from('system_configs')
        .select('value')
        .eq('key', CONFIG_KEY)
        .maybeSingle();

      if (!error && data?.value && data.value.kits && data.value.generalSection) {
        // Cập nhật bộ đệm ngoại tuyến
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data.value));
          } catch (e) {}
        }
        return data.value;
      }
    } catch (err) {
      console.warn('[PythonGuide] Lỗi kết nối Supabase system_configs, chuyển sang bộ đệm:', err);
    }
  }

  // 2. Fallback: Đọc từ Offline Cache trong localStorage
  return getGuideData();
}

/**
 * Database-First: Lưu dữ liệu lên bảng system_configs trên Supabase
 * Đồng thời cập nhật Offline Cache (localStorage) và phát thông báo đa tab
 */
export async function saveGuideData(data) {
  // 1. Luôn cập nhật Offline Cache trước
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  // 2. Phát thông báo qua BroadcastChannel để các tab khác đồng bộ ngay
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(SYNC_CHANNEL);
      channel.postMessage({ type: 'DATA_UPDATED', timestamp: Date.now() });
      channel.close();
    } catch (e) {}
  }

  // 3. Database-First: Upsert vào bảng system_configs trên Supabase
  if (supabaseService && supabaseService.client) {
    try {
      const { error } = await supabaseService.client
        .from('system_configs')
        .upsert({
          key: CONFIG_KEY,
          value: data,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('[PythonGuide] Lỗi upsert system_configs:', error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err) {
      console.error('[PythonGuide] Ngoại lệ khi lưu system_configs:', err);
      return { success: false, error: err.message };
    }
  }

  return { success: true, offline: true };
}

/**
 * Khôi phục toàn bộ tài liệu về cấu hình mặc định ban đầu trên Supabase & Cache
 */
export async function resetGuideData() {
  await saveGuideData(DEFAULT_GUIDE_DATA);
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(SYNC_CHANNEL);
      channel.postMessage({ type: 'DATA_RESET', timestamp: Date.now() });
      channel.close();
    } catch (e) {}
  }
  return DEFAULT_GUIDE_DATA;
}

/**
 * Bảo vệ quyền sửa: Chỉ cho phép admin khi session Supabase Auth có role === 'admin'
 */
export function isUserAdmin(user) {
  if (!user) return false;
  const role = (
    user.role ||
    user.user_metadata?.role ||
    user.app_metadata?.role ||
    ''
  ).trim().toLowerCase();
  return role === 'admin' || role === 'quản trị viên' || role === 'quantrivien' || role === 'superadmin';
}
